import { observer } from 'mobx-react-lite';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useOverlay } from '../../../hooks/useOverlay';
import { useStore } from '../../../hooks/useStore';
import {
  ArrowDownIcon,
  DeleteIconSvg,
  EmployeesSquareIcon,
  ExcelImportIcon,
  ManuellAnlegenIcon,
  PlusIconSvg
} from '../../../lib/icons';
import { DropdownButtonMenu } from '../../tasks/shared/dropdownMenu';
import { useSnackbar } from 'notistack';
import { Tooltip } from '@mui/material';
import { Button } from '../../../lib/components/atomic/button';
import { InformationDialog, Switch } from '../../../lib/components';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/util';

interface AddEmployeeButtonWithDropdownProps {
  hrisStatus?: string;
  hrisSetupStatus?: string | null;
}

export const AddEmployeeButtonWithDropdown: FC<AddEmployeeButtonWithDropdownProps> = observer(
  ({ hrisStatus, hrisSetupStatus }) => {
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const overlay = useOverlay();
    const { companyStore } = useStore();
    const queryClient = useQueryClient();
    const companyId = companyStore.company?.id ?? '';

    const [isMedianConfigured, setIsMedianConfigured] = useState<boolean>(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [deleteConfirmed, setDeleteConfirmed] = useState<boolean>(false);

    const forceSync = async () => {
      try {
        await companyStore.forceSync();
        snackbar.enqueueSnackbar({ message: t('hris:SYNC_STARTED'), variant: 'success' });
        queryClient.invalidateQueries({ queryKey: ['company', companyId, 'settings'] });
      } catch (error) {
        snackbar.enqueueSnackbar({ message: t('account:ERROR_MESSAGE'), variant: 'error' });
      }
    };

    const connectHris = async () => {
      overlay.handleClose();
      try {
        await companyStore.connectKombo();
        await companyStore.fetchCompanySettings();
        queryClient.invalidateQueries({ queryKey: ['company', companyId, 'settings'] });

        const setupStatusData = await queryClient.fetchQuery({
          queryKey: ['company', companyId, 'hris', 'setup-status'],
          queryFn: () =>
            apiClient.companies.companiesHrisHrisSetupStatusRetrieve({ parentCompany: companyId })
        });

        if (setupStatusData?.setup_status === 'INCOMPLETE') {
          snackbar.enqueueSnackbar({
            message: t('hris:HRIS_CONNECT_INCOMPLETE'),
            variant: 'warning'
          });
        } else {
          snackbar.enqueueSnackbar({ message: t('hris:HRIS_CONNECTED'), variant: 'success' });
        }
      } catch (error) {
        snackbar.enqueueSnackbar({ message: t('account:ERROR_MESSAGE'), variant: 'error' });
      }
    };

    const confirmDeleteHris = async () => {
      setDeleteDialogOpen(false);
      setDeleteConfirmed(false);
      try {
        await companyStore.deleteHrisIntegration();
        await companyStore.fetchCompanySettings();
        snackbar.enqueueSnackbar({
          message: t('hris:INTEGRATION_DELETED'),
          variant: 'success'
        });
        queryClient.invalidateQueries({ queryKey: ['company', companyId, 'settings'] });
      } catch (error) {
        snackbar.enqueueSnackbar({ message: t('account:ERROR_MESSAGE'), variant: 'error' });
      }
    };

    const items = [
      {
        onclick: () => navigate('/actions/create-employee'),
        icon: <ManuellAnlegenIcon />,
        text: t('employee:CREATE_MANUAL')
      },
      {
        onclick: () => navigate('/actions/create-import/employees/1'),
        icon: <ExcelImportIcon />,
        text: t('employee:IMPORT_EMPLOYEES')
      },
      {
        onclick: connectHris,
        icon: <EmployeesSquareIcon />,
        text:
          hrisStatus && ['CONNECTION_COMPLETE', 'PAUSED', 'CREATED'].includes(hrisStatus)
            ? t('hris:SWITCH_HRIS')
            : t('hris:CONNECT_HRIS')
      }
    ];

    if (hrisStatus && ['CONNECTION_COMPLETE', 'PAUSED', 'CREATED'].includes(hrisStatus)) {
      items.push({
        onclick: () => {
          overlay.handleClose();
          setDeleteDialogOpen(true);
        },
        icon: <DeleteIconSvg />,
        text: t('hris:DELETE_HRIS_INTEGRATION')
      });
    }

    useEffect(() => {
      const abortController = new AbortController();
      const fn = async () => {
        try {
          setIsMedianConfigured(await companyStore.isMedianConfigured());
        } catch (error) {
          if (!abortController.signal.aborted) {
            console.error(error);
          }
        }
      };
      fn();
      return () => abortController.abort();
    }, [companyStore.settings?.payroll_system]);

    return (
      <>
        {hrisStatus &&
          (['CONNECTION_COMPLETE', 'PAUSED'].includes(hrisStatus) ||
            (hrisStatus === 'CREATED' && hrisSetupStatus !== 'INCOMPLETE')) && (
            <Tooltip
              title={hrisStatus === 'PAUSED' ? t('hris:PAUSE_SYNC_NOTE') : ''}
              placement="bottom"
              arrow
            >
              <span>
                <Button
                  variant="secondary"
                  onClick={() => forceSync()}
                  disabled={hrisStatus === 'PAUSED'}
                  className="mr-2"
                >
                  {t('hris:SYNCHRONIZE')}
                </Button>
              </span>
            </Tooltip>
          )}
        {!isMedianConfigured ? (
          <>
            <Button onClick={(e) => overlay.handleClick(e)} className="ripple">
              <PlusIconSvg /> {t('template:ADD')} <ArrowDownIcon />
            </Button>
            <DropdownButtonMenu
              open={overlay.isOpen}
              anchorEl={overlay.anchor}
              onClose={overlay.handleClose}
              items={items}
            />
          </>
        ) : (
          <></>
        )}
        <InformationDialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setDeleteConfirmed(false);
          }}
          onCancel={() => {
            setDeleteDialogOpen(false);
            setDeleteConfirmed(false);
          }}
          onConfirm={confirmDeleteHris}
          title={t('hris:DELETE_HRIS_INTEGRATION')}
          confirmButtonText={t('hris:DELETE_INTEGRATION')}
          cancelButtonText={t('common:CANCEL')}
          confirmButtonDisabled={!deleteConfirmed}
          confirmButtonColor="error"
          content={
            <div className="space-y-2">
              <div>{t('hris:DELETE_INTEGRATION_NOTE')}</div>
              <Switch
                label={t('hris:DELETE_INTEGRATION_CONFIRM')}
                smallLabel
                onChange={(_event, checked) => setDeleteConfirmed(checked)}
              />
            </div>
          }
        />
      </>
    );
  }
);
